import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  makeStyles
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useIsBreakpointDown } from '~/modules/common/hooks/useBreakpoint';
import useAddUserDefinedSkillOnSubmit from './hooks/useAddUserDefinedSkillOnSubmit';

const useStyles = makeStyles(() => ({
  title: {
    paddingBottom: 0,
    backgroundColor: 'inherit'
  },
  content: {
    paddingTop: 0
  }
}));

const AddUserDefinedSkillDialog = ({
  open,
  initialName = '',
  onClose,
  onSkillAdd
}) => {
  const intl = useIntl();
  const fullScreen = useIsBreakpointDown('xs');
  const classes = useStyles();

  useAddUserDefinedSkillOnSubmit({ onClose, onSkillAdd });

  const initialValues = useMemo(
    () => ({
      name: initialName
    }),
    [initialName]
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .required(intl.formatMessage({ id: 'skillsDropdown.nameRequired' }))
          .max(255, intl.formatMessage({ id: 'skillsDropdown.nameMaxLength' }))
      }),
    [intl]
  );

  const { onSubmit } = useAddUserDefinedSkillOnSubmit({
    onClose,
    onSkillAdd
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth>
      <DialogTitle className={classes.title}>
        <FormattedMessage id="skillsDropdown.addUserDefinedSkill" />
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting
        }) => (
          <Form>
            <DialogContent className={classes.content}>
              <TextField
                id="user-definedskill-name-input"
                name="name"
                label={intl.formatMessage({ id: 'skillsDropdown.name' })}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                fullWidth
                margin="normal"
                required
                variant="filled"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage id="button.cancel" />
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting || Boolean(errors.name) || !values.name}
              >
                <FormattedMessage id="button.add" />
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

AddUserDefinedSkillDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSkillAdd: PropTypes.func,
  initialName: PropTypes.string
};

export default AddUserDefinedSkillDialog;
